import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError, setSessionRefresher } from '@/api/client';
import { purgeSessionCaches } from '@/lib/session-caches';
import {
  clearTabAccessToken,
  getAccessToken,
  getAccessTokenExpiry,
  getTabTenantId,
  setTabAccessToken,
  setTabTenantId,
} from '@/lib/active-tenant';
import type { User, Tenant, AuthResponse, LoginCredentials, CurrentUserResponse, TwoFAChallengeResponse } from '@/types';

export class TwoFARequiredError extends Error {
  constructor(public twoFAToken: string) {
    super('2FA required');
    this.name = 'TwoFARequiredError';
  }
}

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

/**
 * Why the session could not be established, when it is NOT because it
 * ended. `'unavailable'` = the server could not be reached or refused to
 * answer for a reason that says nothing about the credentials (429, 5xx,
 * network drop). The stored tokens are untouched; the view must offer to
 * try again instead of sending the user to the login screen, which would
 * claim an expiry that did not happen.
 */
export type SessionError = 'unavailable';

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  permissions: string[];
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionError: SessionError | null;
}

interface LoginWithTokensPayload {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
  user: User;
  tenant: Tenant;
}

/**
 * Switched session (firm portal): POST /auth/switch-tenant returns a NEW
 * access token for the target dossier with the MAPPED role's permissions
 * (capped at ACCOUNTANT), and no refresh token or user object. The active
 * tenant and permissions are replaced in memory, the `user` object kept.
 * A refresh sends this tab's access token; the API keeps the client file
 * while its step-up is within its window and otherwise returns home, which
 * `performRefresh` leaves to the user to accept (`renewedTenant`).
 */
interface SwitchedSessionPayload {
  accessToken: string;
  expiresAt: string;
  tenant: Tenant;
  permissions: string[];
  /** Firm the access was granted through; null for a direct grant or the home tenant. */
  firm: SwitchedFirm | null;
}

export interface SwitchedFirm {
  id: string;
  name: string;
}

interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  loginWithTokens: (payload: LoginWithTokensPayload) => void;
  applySwitchedSession: (payload: SwitchedSessionPayload) => void;
  /**
   * Firm behind the active client file, from the switch that opened it;
   * null at home, for a direct grant, or once the active tenant changed.
   */
  switchedFirm: SwitchedFirm | null;
  /**
   * Home tenant a refresh brought this tab's token back to while its
   * screens still show a client file; null otherwise. Requests stay held
   * until the user adopts it (`adoptRenewedSession`) or switches back to
   * the file with a new step-up.
   */
  renewedTenant: Tenant | null;
  /** Makes `renewedTenant` this tab's tenant, dropping the file's screens. */
  adoptRenewedSession: () => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  /** Re-run the startup rehydration after a `sessionError`. */
  retrySession: () => void;
}

// ─────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────

const REFRESH_TOKEN_KEY = 'refreshToken';

/** localStorage key an explicit logout writes, so every tab ends its session. */
const SESSION_ENDED_KEY = 'efacture:session-ended';

/** Web Lock that serialises refreshes across the tabs of the origin. */
const REFRESH_LOCK_NAME = 'efacture:auth-refresh';

const REFRESH_THRESHOLD_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry

/**
 * Minimum gap between two refreshes the timer fires for an already-due
 * token. A token lifetime at or under REFRESH_THRESHOLD_MS, or a client
 * clock running that far ahead, makes every new token due on arrival.
 */
const MIN_DUE_REFRESH_GAP_MS = 60 * 1000;

/**
 * Backoff between attempts when the server is momentarily unreachable.
 *
 * WHY A SECOND RETRY POLICY IS NEEDED HERE. React Query already retries
 * every screen query once (`createAppQueryClient`, `retry: 1`), but the two
 * calls that decide whether a session lives — `GET /auth/me` on startup and
 * the scheduled `POST /auth/refresh` — are raw `apiClient` calls made
 * outside React Query, so no policy covers them. This is the gap, not a
 * duplicate.
 *
 * The window is deliberately longer than the rate limiter's: the API caps
 * at `RATE_LIMIT_MAX` requests per ONE minute (`app.ts`), so a client that
 * gives up in five seconds gives up before the quota can possibly reset.
 * 2 s + 5 s + 15 s + 45 s spans past the window without hammering it.
 */
const TRANSIENT_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 45_000];

/**
 * Does this failure mean the SESSION is over?
 *
 * Only 401 (no longer authenticated) and 403 (no longer allowed) do. A 429
 * says the quota is spent — several workstations behind one NAT exit share
 * it; a 5xx says the server is unwell; a rejected `fetch` says the network
 * dropped. None of those is an expiry, and treating them as one logs out a
 * user in the middle of typing an invoice.
 */
function isSessionExpired(error: unknown): boolean {
  // Reuses `ApiError`'s own predicates rather than restating 401/403 here.
  return error instanceof ApiError && (error.isAuthError() || error.isForbidden());
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Runs `refresh` while holding a lock shared by every tab of the origin.
 * The tabs share one rotating refresh token and the API revokes its whole
 * family when a rotated token is presented again, so `refresh` reads the
 * token inside the lock, after the previous holder stored its successor.
 * Without the Web Locks API it runs unlocked.
 */
async function withRefreshLock<T>(refresh: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks || typeof locks.request !== 'function') return refresh();
  return await locks.request(REFRESH_LOCK_NAME, refresh);
}

// ─────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─────────────────────────────────────────────────────────────
// Storage Helpers
// ─────────────────────────────────────────────────────────────

// The access token and its expiry belong to the tab (`@/lib/active-tenant`).
// The refresh token is shared by the tabs when persistent (localStorage) and
// kept by the tab otherwise (sessionStorage).

function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(REFRESH_TOKEN_KEY);
}

function setStoredTokens(
  accessToken: string,
  refreshToken: string,
  expiresAt: string,
  persistent: boolean
): void {
  const storage = persistent ? localStorage : sessionStorage;
  setTabAccessToken(accessToken, expiresAt);
  storage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/** Stores a switched session's access token in this tab and makes its dossier the tab's. */
function storeSwitchedSession(payload: SwitchedSessionPayload): void {
  setTabTenantId(payload.tenant.id);
  setTabAccessToken(payload.accessToken, payload.expiresAt);
}

function clearStoredTokens(): void {
  clearTabAccessToken();
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    permissions: [],
    isLoading: true,
    isAuthenticated: false,
    sessionError: null,
  });
  const queryClient = useQueryClient();
  // Bumped every time a token is stored (login, refresh, loginWithTokens,
  // applySwitchedSession) — the auto-refresh timer effect depends on it so
  // it reschedules from the NEW expiry after every refresh, not only the
  // session's first one (state.isAuthenticated alone stays true → true
  // across a refresh, so the effect would not re-run on that field).
  const [tokenVersion, setTokenVersion] = useState(0);
  // The firm is recorded with the tenant it was returned for, so a later
  // refresh or login that changes the active tenant voids it.
  const [switched, setSwitched] = useState<{ tenantId: string; firm: SwitchedFirm | null } | null>(
    null
  );
  const [renewed, setRenewed] = useState<{ tenant: Tenant; permissions: string[] } | null>(null);

  // Every path that sets the active tenant goes through `enterTenant`. A
  // change of tenant inside a session (switch, back home, a refresh that
  // returns home) drops the query cache and the assistant history, which
  // hold the previous tenant's data.
  const activeTenantRef = useRef<string | null>(null);
  const enterTenant = useCallback(
    (tenantId: string | null) => {
      const previous = activeTenantRef.current;
      activeTenantRef.current = tenantId;
      if (previous !== null && tenantId !== null && previous !== tenantId) {
        queryClient.clear();
        sessionStorage.removeItem('nlp_history');
      }
    },
    [queryClient]
  );

  // ─────────────────────────────────────────────────────────
  // Session Teardown
  // ─────────────────────────────────────────────────────────
  //
  // A-3: the query cache MUST NOT outlive a session. No query key
  // carries a tenantId ('clients'/'list'/{}, 'articles'/'list'/{}, ...),
  // so a cache surviving a session end is served verbatim to whoever
  // logs in next — including a different tenant. Every path that ends a
  // session (explicit logout, expired refresh token, failed startup
  // rehydration) goes through this one function. `keepSharedLogin` ends it
  // in this tab only, leaving the shared refresh token to its owner.

  const endSession = useCallback((keepSharedLogin = false) => {
    if (keepSharedLogin) clearTabAccessToken();
    else clearStoredTokens();
    activeTenantRef.current = null;
    setTabTenantId(null);
    setRenewed(null);
    queryClient.clear();
    setState({
      user: null,
      tenant: null,
      permissions: [],
      isLoading: false,
      isAuthenticated: false,
      sessionError: null,
    });
  }, [queryClient]);

  // ─────────────────────────────────────────────────────────
  // Refresh Token Logic
  // ─────────────────────────────────────────────────────────

  // Single-flight: the API revokes the whole token family when a rotated
  // refresh token is presented twice, so concurrent callers (timer,
  // visibilitychange, rehydration) must share one request.
  const refreshInFlight = useRef<Promise<void> | null>(null);
  // The account this tab is signed in as, read by a refresh.
  const userIdRef = useRef<string | null>(null);
  useEffect(() => {
    userIdRef.current = state.user?.id ?? null;
  }, [state.user]);
  // When the timer effect last fired a refresh for an already-due token.
  const lastDueRefreshAtRef = useRef<number | null>(null);

  const performRefresh = useCallback(async (): Promise<void> => {
    const response = await withRefreshLock(async () => {
      const storedRefreshToken = getStoredRefreshToken();
      if (!storedRefreshToken) {
        throw new Error('Aucun jeton de rafraîchissement disponible');
      }

      // This tab's access token lets the API keep its client file open
      // across the refresh (within its step-up window).
      const currentAccessToken = getAccessToken();
      const answer = await apiClient.post<AuthResponse>('/auth/refresh', {
        refreshToken: storedRefreshToken,
        ...(currentAccessToken ? { accessToken: currentAccessToken } : {}),
      });

      const isPersistent = localStorage.getItem(REFRESH_TOKEN_KEY) !== null;
      setStoredTokens(answer.accessToken, answer.refreshToken, answer.expiresAt, isPersistent);
      return answer;
    });
    setTokenVersion((v) => v + 1);

    // Another tab signed in as another account over the shared refresh
    // token: this tab's session ends here, without touching that login.
    if (userIdRef.current !== null && response.user.id !== userIdRef.current) {
      endSession(true);
      return;
    }

    // Back home while the screens show a client file: the step-up window
    // has passed or the access is gone. The screens and any entry in
    // progress stay, and requests are held, until the user goes back to the
    // file (new step-up) or adopts the home tenant.
    const tabTenantId = getTabTenantId();
    if (tabTenantId !== null && tabTenantId !== response.tenant.id) {
      setRenewed({ tenant: response.tenant, permissions: response.user.permissions });
      setState((prev) => ({
        ...prev,
        user: response.user,
        isLoading: false,
        isAuthenticated: true,
        sessionError: null,
      }));
      return;
    }

    setTabTenantId(response.tenant.id);
    enterTenant(response.tenant.id);
    setSwitched(
      response.tenant.id !== response.user.tenantId
        ? { tenantId: response.tenant.id, firm: response.firm ?? null }
        : null
    );
    setRenewed(null);
    setState({
      user: response.user,
      tenant: response.tenant,
      permissions: response.user.permissions,
      isLoading: false,
      isAuthenticated: true,
      sessionError: null,
    });
  }, [enterTenant, endSession]);

  const refreshToken = useCallback((): Promise<void> => {
    if (refreshInFlight.current) return refreshInFlight.current;
    const attempt = performRefresh();
    refreshInFlight.current = attempt;
    const release = () => {
      if (refreshInFlight.current === attempt) refreshInFlight.current = null;
    };
    attempt.then(release, release);
    return attempt;
  }, [performRefresh]);

  // A request answering TOKEN_EXPIRED (a tab back from sleep) renews
  // through the same single-flight refresh, then is sent again. A refresh
  // answering 401/403 ends the session here; the client then leaves for
  // the login screen.
  useEffect(() => {
    setSessionRefresher(async () => {
      try {
        if (!getStoredRefreshToken()) throw new ApiError('Session expirée', 'UNAUTHORIZED', 401);
        await refreshToken();
      } catch (error) {
        if (isSessionExpired(error)) endSession();
        throw error;
      }
    });
    return () => setSessionRefresher(null);
  }, [refreshToken, endSession]);

  /**
   * Scheduled refresh, tolerant of a server that is momentarily out of
   * reach. Ends the session ONLY on a real expiry (401/403).
   *
   * When the retries run out we keep the session open rather than tear it
   * down: the user is in front of a working screen, possibly mid-entry, and
   * the access token is not necessarily dead yet. If it truly has lapsed,
   * the next request that genuinely answers 401 ends the session through
   * the existing path — a real answer, not a guess.
   */
  const refreshWithRetry = useCallback(async () => {
    for (let attempt = 0; attempt <= TRANSIENT_RETRY_DELAYS_MS.length; attempt++) {
      // The loop sleeps up to 67 s in total; the session can end under it
      // (an explicit logout, a 401 elsewhere). Nothing cancels an
      // already-running loop — the effect cleanup only clears the pending
      // `setTimeout` — so each iteration re-checks that there is still a
      // session to refresh. Without this, a logged-out tab keeps calling
      // `/auth/refresh`, and the "no refresh token" error it then raises is
      // a plain `Error`, not an `ApiError`: it would be filed as transient
      // and retried again.
      if (!getStoredRefreshToken()) return;

      try {
        await refreshToken();
        return;
      } catch (error) {
        if (isSessionExpired(error)) {
          endSession();
          return;
        }
        const wait = TRANSIENT_RETRY_DELAYS_MS[attempt];
        if (wait === undefined) break;
        await delay(wait);
      }
    }
    setState((prev) => (prev.isAuthenticated ? { ...prev, sessionError: 'unavailable' } : prev));
  }, [refreshToken, endSession]);

  // ─────────────────────────────────────────────────────────
  // Auto-refresh Timer
  // ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!state.isAuthenticated) {
      return;
    }

    const tokenExpiry = getAccessTokenExpiry();
    if (!tokenExpiry) {
      return;
    }

    const expiresAt = new Date(tokenExpiry).getTime();
    const now = Date.now();
    const untilThreshold = expiresAt - now - REFRESH_THRESHOLD_MS;
    const isDue = untilThreshold <= 0;
    // A due token refreshes now, unless a due refresh fired less than
    // MIN_DUE_REFRESH_GAP_MS ago: then it waits out the rest of that gap.
    const lastDueRefreshAt = lastDueRefreshAtRef.current;
    const wait = !isDue
      ? untilThreshold
      : lastDueRefreshAt === null
        ? 0
        : lastDueRefreshAt + MIN_DUE_REFRESH_GAP_MS - now;
    const fire = () => {
      if (isDue) lastDueRefreshAtRef.current = Date.now();
      void refreshWithRetry();
    };

    if (wait <= 0) {
      fire();
      return;
    }

    const timerId = setTimeout(fire, wait);

    return () => clearTimeout(timerId);
    // `tokenVersion` is the reason this effect re-runs after every
    // successful refresh (see its declaration) — the token's expiry
    // changed, but `state.isAuthenticated` alone did not (true → true).
  }, [state.isAuthenticated, tokenVersion, refreshWithRetry]);

  // Safety net for a timer a background tab throttled or a laptop sleep
  // paused: re-check the stored expiry whenever the tab becomes visible
  // again, and refresh immediately if it is already due.
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible' || !state.isAuthenticated) return;
      const tokenExpiry = getAccessTokenExpiry();
      if (!tokenExpiry) return;
      const timeUntilRefresh = new Date(tokenExpiry).getTime() - Date.now() - REFRESH_THRESHOLD_MS;
      if (timeUntilRefresh <= 0) void refreshWithRetry();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.isAuthenticated, refreshWithRetry]);

  // ─────────────────────────────────────────────────────────
  // Load User on Mount
  // ─────────────────────────────────────────────────────────

  /**
   * One rehydration attempt. Reports WHY it failed instead of collapsing
   * every failure into "log out", which is what the two bare `catch`
   * blocks here used to do.
   */
  const rehydrateOnce = useCallback(async (): Promise<
    'ok' | 'expired' | 'unavailable'
  > => {
    try {
      // Pattern C: /auth/me returns same structure as login/refresh
      const response = await apiClient.get<CurrentUserResponse>('/auth/me');
      setTabTenantId(response.tenant.id);
      enterTenant(response.tenant.id);
      setSwitched(
        response.tenant.id !== response.user.tenantId
          ? { tenantId: response.tenant.id, firm: response.firm ?? null }
          : null
      );
      setRenewed(null);
      setState({
        user: response.user,
        tenant: response.tenant,
        permissions: response.user.permissions,
        isLoading: false,
        isAuthenticated: true,
        sessionError: null,
      });
      return 'ok';
    } catch (meError) {
      // A 429 says nothing about the access token. Spending the refresh
      // token on it burns the only credit left AND costs another request
      // against the very quota that is already exhausted.
      if (!isSessionExpired(meError)) return 'unavailable';

      // 401/403: the access token is what expired — this is precisely what
      // the refresh token is for.
      try {
        await refreshToken();
        return 'ok';
      } catch (refreshError) {
        return isSessionExpired(refreshError) ? 'expired' : 'unavailable';
      }
    }
  }, [refreshToken, enterTenant]);

  const loadUser = useCallback(async () => {
    const storedRefreshToken = getStoredRefreshToken();

    if (!storedRefreshToken) {
      setState((prev) => ({ ...prev, isLoading: false, sessionError: null }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true, sessionError: null }));

    for (let attempt = 0; attempt <= TRANSIENT_RETRY_DELAYS_MS.length; attempt++) {
      const outcome = await rehydrateOnce();
      if (outcome === 'ok') return;
      if (outcome === 'expired') {
        endSession();
        return;
      }
      const wait = TRANSIENT_RETRY_DELAYS_MS[attempt];
      if (wait === undefined) break;
      await delay(wait);
    }

    // Out of reach, not expired. The tokens stay: the next attempt (the
    // « Réessayer » button, a reload) can still succeed. `sessionError`
    // exists so the router shows that story instead of the login screen,
    // which would announce an expiry that never happened.
    setState((prev) => ({
      ...prev,
      isLoading: false,
      isAuthenticated: false,
      sessionError: 'unavailable',
    }));
  }, [rehydrateOnce, endSession]);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  // ─────────────────────────────────────────────────────────
  // Login
  // ─────────────────────────────────────────────────────────

  const login = useCallback(async (credentials: LoginCredentials) => {
    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const { rememberMe, ...loginData } = credentials;
      const response = await apiClient.post<AuthResponse | TwoFAChallengeResponse>('/auth/login', loginData);

      // Check if 2FA is required
      if ('requires2FA' in response && response.requires2FA) {
        setState((prev) => ({ ...prev, isLoading: false }));
        throw new TwoFARequiredError(response.twoFAToken);
      }

      const authResponse = response as AuthResponse;
      setTabTenantId(authResponse.tenant.id);
      enterTenant(authResponse.tenant.id);

      // Use localStorage for persistent storage if rememberMe is checked
      // Otherwise use sessionStorage (cleared when browser tab closes)
      setStoredTokens(
        authResponse.accessToken,
        authResponse.refreshToken,
        authResponse.expiresAt,
        rememberMe ?? false
      );
      setTokenVersion((v) => v + 1);

      setState({
        user: authResponse.user,
        tenant: authResponse.tenant,
        permissions: authResponse.user.permissions,
        isLoading: false,
        isAuthenticated: true,
        sessionError: null,
      });
    } catch (error) {
      setState((prev) => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [enterTenant]);

  // ─────────────────────────────────────────────────────────
  // Login with Tokens (for passkey auth)
  // ─────────────────────────────────────────────────────────

  const loginWithTokens = useCallback((payload: LoginWithTokensPayload) => {
    setTabTenantId(payload.tenant.id);
    enterTenant(payload.tenant.id);
    setStoredTokens(
      payload.accessToken,
      payload.refreshToken,
      payload.expiresAt,
      true // persistent storage for passkey logins
    );
    setTokenVersion((v) => v + 1);

    setState({
      user: payload.user,
      tenant: payload.tenant,
      permissions: payload.user.permissions,
      isLoading: false,
      isAuthenticated: true,
      sessionError: null,
    });
  }, [enterTenant]);

  // ─────────────────────────────────────────────────────────
  // Bascule de dossier (portail cabinet) — SANS rechargement dur
  // ─────────────────────────────────────────────────────────
  //
  // The switch updates the state IN MEMORY (active tenant, capped
  // permissions, firm) and replaces this tab's access token, with no hard
  // reload. The caller (TenantSwitcher) then clears the react-query cache
  // and navigates to « / » (soft navigation, AuthProvider stays mounted).
  // A later reload rehydrates the file from GET /auth/me, which answers
  // the token's tenant and firm.

  const applySwitchedSession = useCallback((payload: SwitchedSessionPayload) => {
    enterTenant(payload.tenant.id);
    // The new token's expiry goes with it, so the auto-refresh timer is
    // scheduled from it.
    storeSwitchedSession(payload);
    setTokenVersion((v) => v + 1);
    setSwitched({ tenantId: payload.tenant.id, firm: payload.firm });
    setRenewed(null);

    setState((prev) => ({
      ...prev,
      tenant: payload.tenant,
      permissions: payload.permissions,
    }));
  }, [enterTenant]);

  const adoptRenewedSession = useCallback(() => {
    if (!renewed) return;
    setTabTenantId(renewed.tenant.id);
    enterTenant(renewed.tenant.id);
    setSwitched(null);
    setRenewed(null);
    setState((prev) => ({ ...prev, tenant: renewed.tenant, permissions: renewed.permissions }));
  }, [renewed, enterTenant]);

  const switchedFirm =
    switched !== null && state.tenant !== null && switched.tenantId === state.tenant.id
      ? switched.firm
      : null;

  // ─────────────────────────────────────────────────────────
  // Session ended in another tab
  // ─────────────────────────────────────────────────────────
  //
  // An explicit logout elsewhere, or the shared refresh token removed from
  // localStorage, ends this tab's session too: its own access token would
  // otherwise keep the dossier on screen until it expires.

  useEffect(() => {
    if (!state.isAuthenticated) return;
    function onStorage(event: StorageEvent) {
      if (event.storageArea !== localStorage) return;
      const loggedOut = event.key === SESSION_ENDED_KEY && event.newValue !== null;
      const refreshTokenGone =
        (event.key === REFRESH_TOKEN_KEY || event.key === null) && getStoredRefreshToken() === null;
      if (!loggedOut && !refreshTokenGone) return;
      endSession();
      sessionStorage.removeItem('nlp_history');
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [state.isAuthenticated, endSession]);

  // ─────────────────────────────────────────────────────────
  // Logout
  // ─────────────────────────────────────────────────────────

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch {
      // Ignore errors during logout — a network failure must never leave
      // the browser holding the session's data.
    } finally {
      // Tokens + in-memory query cache + auth state.
      endSession();
      // The other tabs hold their own access tokens: they end their
      // session on this signal.
      try {
        localStorage.setItem(SESSION_ENDED_KEY, String(Date.now()));
        localStorage.removeItem(SESSION_ENDED_KEY);
      } catch {
        // Storage blocked: the other tabs end at their next refresh.
      }
      // Purge l'historique des requêtes NLP (voir useNLPQuery) : il peut
      // contenir des données métier et ne doit pas survivre à la session.
      sessionStorage.removeItem('nlp_history');
      // Durable caches (IndexedDB reference data, service-worker
      // api-cache). Only on an EXPLICIT logout: a failed startup
      // rehydration (offline cold start) also ends the session, but
      // dropping the offline reference cache there would break the
      // feature it exists for, and leaks nothing new — the same user is
      // still the one in front of the machine.
      await purgeSessionCaches();
    }
  }, [endSession]);

  // ─────────────────────────────────────────────────────────
  // Context Value
  // ─────────────────────────────────────────────────────────

  const retrySession = useCallback(() => {
    void loadUser();
  }, [loadUser]);

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      loginWithTokens,
      applySwitchedSession,
      switchedFirm,
      renewedTenant: renewed?.tenant ?? null,
      adoptRenewedSession,
      logout,
      refreshToken,
      retrySession,
    }),
    [
      state,
      login,
      loginWithTokens,
      applySwitchedSession,
      switchedFirm,
      renewed,
      adoptRenewedSession,
      logout,
      refreshToken,
      retrySession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }

  return context;
}

export { AuthContext };
